import { capPointReward } from "./economy.js";
import {
  defaultRouletteConfiguration,
  validateRouletteConfiguration,
  validateRouletteWager,
  type RouletteConfiguration,
  type RouletteCommitResult,
} from "./roulette.js";

export type SlotsConfiguration = RouletteConfiguration;
export type SlotsCommitResult = RouletteCommitResult;
export type SlotSymbol =
  "rex" | "raptor" | "trike" | "amber" | "fern" | "egg" | "wild" | "fossil";
export const SLOT_VERSION = "fossil-hunt-1";
export const SLOT_MAX_WIN = 1_000;
export const SLOT_MAX_FREE_SPINS = 20;
export const SLOT_SYMBOLS: SlotSymbol[] = [
  "rex",
  "raptor",
  "trike",
  "amber",
  "fern",
  "egg",
  "wild",
  "fossil",
];
export const SLOT_WEIGHTS = [7, 10, 14, 18, 24, 21, 3, 3] as const;
/** Multiples of one line's stake, for 3, 4, or 5 consecutive symbols from the left. */
export const SLOT_PAYTABLE: Record<
  Exclude<SlotSymbol, "fossil">,
  readonly [number, number, number]
> = {
  rex: [35, 140, 420],
  raptor: [21, 70, 210],
  trike: [14, 42, 140],
  amber: [11, 28, 84],
  fern: [6, 14, 42],
  egg: [7, 21, 56],
  wild: [70, 280, 700],
};
export const SLOT_LINES = [
  [1, 1, 1, 1, 1],
  [0, 0, 0, 0, 0],
  [2, 2, 2, 2, 2],
  [0, 1, 2, 1, 0],
  [2, 1, 0, 1, 2],
  [0, 0, 1, 2, 2],
  [2, 2, 1, 0, 0],
  [1, 0, 0, 0, 1],
  [1, 2, 2, 2, 1],
  [0, 1, 1, 1, 0],
] as const;
export interface SlotLineWin {
  line: number;
  symbol: Exclude<SlotSymbol, "fossil">;
  count: number;
  payout: number;
}
export interface SlotRound {
  reels: SlotSymbol[][];
  mode: "base" | "free";
  multiplier: number;
  wins: SlotLineWin[];
  payout: number;
  fossils: number;
  freeSpinsAdded: number;
}
export interface SlotsSpin {
  id: string;
  serverId: string;
  userId: string;
  requestId: string;
  wager: number;
  payout: number;
  rounds: SlotRound[];
  mathVersion: string;
  capped: boolean;
  createdAt: Date;
}

/** Call under the wallet lock, using the balance after deducting this wager. */
export function settleSlotsWalletPayout(spin: SlotsSpin, balanceAfterWager: number, maxPoints: number): SlotsSpin {
  return { ...spin, payout: capPointReward(balanceAfterWager, spin.payout, maxPoints) };
}

/** Rounds preserve the rolled result; the persisted total is the actual credit. */
export function slotsWalletCapApplied(spin: Pick<SlotsSpin, "rounds" | "payout">): boolean {
  return spin.rounds.reduce((sum, round) => sum + round.payout, 0) > spin.payout;
}
export const defaultSlotsConfiguration = (
  serverId: string,
  now: Date,
): SlotsConfiguration => ({
  ...defaultRouletteConfiguration(serverId, now),
  maximumWager: 5_000,
  dailyWagerLimit: 50_000,
});
export function validateSlotsConfiguration(
  config: SlotsConfiguration,
): string[] {
  return [
    ...validateRouletteConfiguration(config),
    ...(config.minimumWager % 10 || config.wagerStep % 10
      ? ["Minimum and increment must be multiples of 10 for the ten paylines."]
      : []),
    ...(config.maximumWager > 50_000
      ? ["Maximum wager cannot exceed 50,000 Sticks."]
      : []),
  ];
}
export function validateSlotsWager(
  wager: number,
  config: SlotsConfiguration,
): string | undefined {
  return (
    validateRouletteWager(wager, config) ??
    (wager % 10 ? "Use a multiple of 10 Sticks." : undefined)
  );
}
export function evaluateSlotLines(
  reels: SlotSymbol[][],
  wager: number,
  multiplier: number,
): SlotLineWin[] {
  if (
    reels.length !== 5 ||
    reels.some(
      (reel) =>
        reel.length !== 3 ||
        reel.some((symbol) => !SLOT_SYMBOLS.includes(symbol)),
    )
  )
    throw new Error("Invalid reel grid.");
  if (
    !Number.isSafeInteger(wager) ||
    wager < 10 ||
    wager > 50_000 ||
    wager % 10 ||
    !Number.isInteger(multiplier) ||
    multiplier < 1 ||
    multiplier > 5
  )
    throw new Error("Invalid slot stake or multiplier.");
  return SLOT_LINES.flatMap((rows, line) => {
    const symbols = rows.map((row, reel) => reels[reel]![row]!);
    let best: SlotLineWin | undefined;
    for (const symbol of Object.keys(SLOT_PAYTABLE) as Array<
      keyof typeof SLOT_PAYTABLE
    >) {
      let count = 0;
      for (const landed of symbols) {
        if (landed !== symbol && landed !== "wild") break;
        count++;
      }
      if (count < 3) continue;
      const payout =
        (wager / 10) * SLOT_PAYTABLE[symbol][count - 3]! * multiplier;
      if (!best || payout > best.payout) best = { line, symbol, count, payout };
    }
    return best ? [best] : [];
  });
}
/** The server supplies cryptographic randomInt. All free rounds settle in the same wallet transaction. */
export function createSlotsSpin(
  input: {
    id: string;
    serverId: string;
    userId: string;
    requestId: string;
    wager: number;
    now: Date;
  },
  randomBelow: (max: number) => number,
): SlotsSpin {
  if (
    !Number.isSafeInteger(input.wager) ||
    input.wager < 10 ||
    input.wager > 50_000 ||
    input.wager % 10
  )
    throw new Error("Invalid slot wager.");
  const draw = (): SlotSymbol => {
    let roll = randomBelow(100);
    if (!Number.isInteger(roll) || roll < 0 || roll >= 100)
      throw new Error("Invalid random source.");
    for (let i = 0; i < SLOT_WEIGHTS.length; i++) {
      roll -= SLOT_WEIGHTS[i]!;
      if (roll < 0) return SLOT_SYMBOLS[i]!;
    }
    throw new Error("Invalid reel weights.");
  };
  const rounds: SlotRound[] = [];
  let remaining = 0,
    awarded = 0,
    multiplier = 2,
    payout = 0,
    capped = false;
  do {
    const free = rounds.length > 0;
    if (free) remaining--;
    const reels = Array.from({ length: 5 }, () =>
      Array.from({ length: 3 }, draw),
    );
    const fossils = reels.flat().filter((symbol) => symbol === "fossil").length;
    const wins = evaluateSlotLines(reels, input.wager, free ? multiplier : 1);
    const rawPayout = wins.reduce((sum, win) => sum + win.payout, 0);
    const roundPayout = Math.min(
      rawPayout,
      input.wager * SLOT_MAX_WIN - payout,
    );
    capped ||= roundPayout < rawPayout;
    payout += roundPayout;
    const freeSpinsAdded =
      fossils >= 3
        ? Math.min(
            free ? 3 : fossils === 3 ? 8 : fossils === 4 ? 10 : 12,
            SLOT_MAX_FREE_SPINS - awarded,
          )
        : 0;
    awarded += freeSpinsAdded;
    remaining += freeSpinsAdded;
    rounds.push({
      reels,
      mode: free ? "free" : "base",
      multiplier: free ? multiplier : 1,
      wins,
      payout: roundPayout,
      fossils,
      freeSpinsAdded,
    });
    if (free && reels.flat().includes("wild"))
      multiplier = Math.min(5, multiplier + 1);
    if (payout >= input.wager * SLOT_MAX_WIN) {
      capped = true;
      break;
    }
  } while (remaining > 0);
  const { now, ...identity } = input;
  return {
    ...identity,
    payout,
    rounds,
    mathVersion: SLOT_VERSION,
    capped,
    createdAt: now,
  };
}
